/**
 * Display charges for subscriptions, payments, and other transactions for the account.
 * Use cases currently:
 *  Reseller can view 
 *  - their own charges (here from MyCharges)
 *  - the charges of their customers (here from CustomerFinance)
 *  Customers can view their own charges -> moved to ChargesCustomer for different query.
 *  Admin can view
 *  - Charges facilitated by a Reseller with ability to narrow to app/customer (here from Reseller page)
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, TextField } from '@mui/material';
import { TablePagination } from '@mui/material';
import GetApp from '@mui/icons-material/GetApp';
import ChargesList from './ChargesList';
import ChargesFetch from './ChargesFetch';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import SearchField from '../ui/SearchField';
import AppBindStyles from '../../AppBindStyles';
import { addDays, format, isValid, startOfDay, subMonths } from 'date-fns';

const abEnvironment = new AbEnvironment();
const ChargesQuery = graphql`
query ChargesQuery($startDate: Datetime, $endDate: Datetime, $appName: String, $accountId: UUID, $pAccountId: UUID, $pOrder: Int, $first: Int, $offset: Int) {
	getchargesexternalsandpaymentswithsummary(startTs: $startDate, endTs: $endDate, appName: $appName, accountId: $accountId, pAccountId: $pAccountId, paymentOrder: $pOrder, first: $first, offset: $offset) {
		totalCount
		...ChargesList_cl
	}
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    }
});

class Charges extends Component {

  constructor(props) {
    super(props);

    // Default toDate
    let cToDate = new Date();
    let tDate = localStorage.getItem('cToDate');
    if (tDate != null) {
      cToDate = new Date(tDate);
    }
    //search todate should be "start of next day"
    let searchToDateUtc = startOfDay(addDays(cToDate, 1)).toISOString();

    let cFromDate = new Date();
    let fDate = localStorage.getItem('cFromDate');
    if (fDate != null) {
      cFromDate = new Date(fDate);
    } else {
      cFromDate = subMonths(cToDate, 1);
    }
    //search fromdate should be "start of day"
    let searchFromDateUtc = startOfDay(cFromDate).toISOString();

    this.state = {
      startDate: cFromDate,
      queryStartDateUtc: searchFromDateUtc,
      endDate: cToDate,
      queryEndDateUtc: searchToDateUtc,
      appName: '', 
      accountId: this.props.customerAccountId,
      pAccountId: this.props.pAccountId,
      page: 0,
      rowsPerPage: 10,
    };  
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  setFromDate(theDate) {
    let vdate = new Date(theDate); // Start of date local timezone.
    if (isValid(vdate)) {
      localStorage.setItem('cFromDate', vdate);
      let searchDateUtc = startOfDay(vdate).toISOString();
      this.setState({ startDate: vdate, queryStartDateUtc: searchDateUtc, page : 0 });
    }
  }

  setToDate(theDate) {
    let vdate = new Date(theDate);
    if (isValid(vdate)) {
      localStorage.setItem('cToDate', vdate);
      let searchDateUtc = startOfDay(addDays(vdate, 1)).toISOString();
      this.setState({ endDate: vdate, queryEndDateUtc: searchDateUtc, page : 0 });
    }
  }

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  // The download needs to do another query in case there is more data for
  // the range than is shown on the screen.
  downloadCsv(thePage, theRole, startDate, endDate, appName, accountId, pAccountId, pOrder, first, offset) {

    const { logError } = this.props.auth;

    ChargesFetch.fetch(startDate, endDate, appName, accountId, pAccountId, pOrder, first, offset).toPromise()
    .then(cf => {
      let theCount = cf.getchargesexternalsandpaymentswithsummary.totalCount;
      if (theCount < 1) {
        return;
      }
      let thenodes = cf.getchargesexternalsandpaymentswithsummary.nodes;
      let csv;

      if (thePage === "custCharge") {
        csv = 'Date, App, Billed To Customer, Billed to Me, App Charge, Markup, Fees (AppBind), Fees (Network), Rebate, Profit\n';
        thenodes.map(n=> {
          //transaction time is unzoned, specify as UTC
          let tdate = format(new Date(n.transactionTime+'Z'), 'yyyy-MM-dd');
          let bcc = Number(n.bindCardCharge)
                    .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          let mkp = '';
          if (n.markup !== 0)
          {
            mkp = Number(n.markup)
                    .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }
          let rebate = '';
          if (Number(n.rebate) > 0)
          {
            rebate = Number(n.rebate)
              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }
  
          let billToCust = 0;
          let billToMe = 0;
          let ppFeeCust = 0;
          let ppFeeRes = 0;
          let ppFeeTot = 0;

          let thePayments = n.payments;
          if (thePayments != null && thePayments.length > 0)
          {
            for (let i=0; i<thePayments.length; i++)
            {
              if (thePayments[i].psAccountId === n.customerAccountId)
              {
                billToCust = Number(billToCust) + Number(thePayments[i].paidAmount);
                ppFeeCust = Number(ppFeeCust) + Number(thePayments[i].ppAmount);
              }
              else if (thePayments[i].psAccountId === n.provisioningAccountId)
              {
                billToMe = Number(billToMe) + Number(thePayments[i].paidAmount);
                ppFeeRes = Number(ppFeeRes) + Number(thePayments[i].ppAmount);
              }
            }
          }

          let billedToCust = '';
          if (billToCust > 0) {
            billedToCust = Number(billToCust)
                            .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }

          let billedToMe = '';
          if (billToMe > 0) {
            billedToMe = Number(billToMe)
                            .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }

          ppFeeTot = Number(ppFeeCust) + Number(ppFeeRes);
          let appBindFee = Number(n.appbindFee)
          .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          let ppFee = Number(ppFeeTot)
          .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

          let netToMe = '';
          if (Number(n.markup) > 0)
          {
            netToMe = Number(n.profit)
              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }
            // eslint-disable-next-line
            let csvline = '' + tdate + ',' + n.appName + ',\"' + billedToCust + '\",\"' + billedToMe + '\",\"' + bcc + '\",\"' + mkp + '\",\"' + appBindFee + '\",\"' + ppFee + '\",\"' + rebate + '\",\"' + netToMe + '\"\n';
          csv = csv + csvline;
          return true;
        });
      } else {
        if (theRole === "reseller") {
          csv = 'Date, App, Total Billed, App Charge, Fees (AppBind), Fees (Network)\n';
          thenodes.map(n=> {
            //transaction time is un-zoned, specify as UTC.
            let tdate = format(new Date(n.transactionTime+'Z'), 'yyyy-MM-dd');
            let bcc = Number(n.bindCardCharge)
                      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            let fee = Number(n.appbindFee)
                      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            let billToCust = 0;
            let billToMe = 0;
            let ppFeeCust = 0;
            let ppFeeRes = 0;
            let ppFeeTot = 0;

            let thePayments = n.payments;
            if (thePayments != null && thePayments.length > 0)
            {
              for (let i=0; i<thePayments.length; i++)
              {
                if (thePayments[i].psAccountId === n.customerAccountId)
                {
                  billToCust = Number(billToCust) + Number(thePayments[i].paidAmount);
                  ppFeeCust = Number(ppFeeCust) + Number(thePayments[i].ppAmount);
                }
                else if (thePayments[i].psAccountId === n.provisioningAccountId)
                {
                  billToMe = Number(billToMe) + Number(thePayments[i].paidAmount);
                  ppFeeRes = Number(ppFeeRes) + Number(thePayments[i].ppAmount);
                }
              }
            }

            ppFeeTot = Number(ppFeeCust) + Number(ppFeeRes);
            let ppFee = Number(ppFeeTot)
            .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            let billedToCust = '';
            if (billToCust > 0) {
              billedToCust = Number(billToCust)
                              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            }
            // eslint-disable-next-line
            let csvline = '' + tdate + ',' + n.appName + ',\"' + billedToCust + '\",\"' + bcc + '\",\"' + fee + '\",\"' + ppFee + '\"\n';
            csv = csv + csvline;
            return true;
          });
        } else {

          /* Here we are putting in a bit of a hack.  It needs to be be fixed but tabling it for now.
           For the customer portal, we have charges that were before becoming an authenticated customer and 
           charges after authenticated customer.
           the services and fees then are calculated differently.

           pre transfer, the services and fees could be:
           markup + appbind_fee (if passed through) + CC fee

           post transfer, the services and fees are:
           appbind_fee + CC fee.

           It is simpler just to take the subtraction of the two to show in fees than it is to jockey sum the
           detail.  We can circle back on it.

          */

          csv = 'Date, App, Total Billed, App Charge, Service and Fees\n';
          thenodes.map(n=> {
            //transaction time is unzoned, specify as UTC
            let tdate = format(new Date(n.transactionTime+'Z'), 'yyyy-MM-dd');
            
            let bcc = Number(n.bindCardCharge)
                      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            /*let mkp = Number(n.markup)
                      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});*/

            let billToCust = 0;
            let billToMe = 0;

            let thePayments = n.payments;
            if (thePayments != null && thePayments.length > 0)
            {
              for (let i=0; i<thePayments.length; i++)
              {
                if (thePayments[i].psAccountId === n.customerAccountId)
                {
                  billToCust = Number(billToCust) + Number(thePayments[i].paidAmount);
                }
                else if (thePayments[i].psAccountId === n.provisioningAccountId)
                {
                  billToMe = Number(billToMe) + Number(thePayments[i].paidAmount);
                }
              }
            }

            let billedToCust = '';
            if (billToCust > 0) {
              billedToCust = Number(billToCust)
                              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            }
            let fees = Number(billToCust) - Number(n.bindCardCharge);
            let mkp = Number(fees)
                      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            // eslint-disable-next-line
            let csvline = '' + tdate + ',' + n.appName + ',\"' + billedToCust + '\",\"' + bcc + '\",\"' + mkp + '\"\n';
            csv = csv + csvline;
            return true;
          });

        }
      }

      var hiddenElement = document.createElement('a');
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
      hiddenElement.target = '_blank';
      hiddenElement.download = 'charges.csv';
      hiddenElement.click();
    })
    .catch(err => {
      logError('Exception in Charges.js: ' + err);
      // alert('Error: ' + err);
    });
  }

  render() {
    const { isAuthenticated, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;


    if ((!isAuthenticated() && !isUnauthenticatedCustomer()) || needAssent() ) { 
      return ( <Redirect to='/home' /> );
    }
    
    //set up fields - here means reseller b/c customer viewable pages now go to ChargesCustomer
    const theRole = "reseller";
    
    let theAppSearch = '%';
    if (this.state.appName !== '') {
      theAppSearch = '%' + this.state.appName + '%';
    }

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0) {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    let thePage = this.props.pg;
    if (thePage == null)
      thePage = "custCharge";

    let paymentOrder = null;
    
    return (
      <div className={classes.root}>
        <Grid container spacing={2} alignItems='center' style={AppBindStyles.searchContainerOnPaper}>
          <Grid item>
            <SearchField
              id="appName"
              label="Search by app name"
              value={this.state.appName}
              onChange={this.handleChange('appName')}
            />
          </Grid>
          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker 
              disableFuture
              label="From date"
              value={this.state.startDate}
              onChange={(newValue) => {
                this.setFromDate(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>

          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker 
              disableFuture
              label="To date"
              value={this.state.endDate}
              onChange={(newValue) => {
                this.setToDate(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={() => 
                this.downloadCsv(thePage, theRole, 
                                this.state.queryStartDateUtc, this.state.queryEndDateUtc, theAppSearch,
                                this.state.accountId, this.state.pAccountId, 
                                paymentOrder, 10000, 0)}
            >
              Download charges&nbsp;&nbsp;<GetApp/>
            </Button>
          </Grid>
        </Grid>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ChargesQuery}
          variables={{startDate: this.state.queryStartDateUtc,
                      endDate: this.state.queryEndDateUtc,
                      appName: theAppSearch,
                      accountId: this.state.accountId,
                      pAccountId: this.state.pAccountId,
                      pOrder: paymentOrder,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Determine offset information
              totalCount = props.getchargesexternalsandpaymentswithsummary.totalCount;

              let showCustomerName = false;
              if (this.state.pAccountId != null)
                showCustomerName = true;

              return (
                <div>
                  <ChargesList cl={props.getchargesexternalsandpaymentswithsummary} 
                               theRole={theRole}
                               thePage={thePage}
                               sn={showCustomerName}
                               history={this.props.history}/>
                  <div style={AppBindStyles.paginationTableFooter}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />                        
                  </div>
                </div>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </div>
    );
  }
}
  
Charges.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Charges);
